/**
 * The console half of a CP step-up (kernel apps, WS-9b, `design-docs/WS-9.md` section 3.4).
 *
 * Four CP writes owe a passkey step-up (R-004): a kernel-right request, a kernel-right decision, a
 * release-sources change and a statement signature. release-engine answers the first send of each
 * write with 403 and the `step_up_required` body of `@zion/sdk-contracts`. The console then shows
 * the request that cp-auth-api stores, asks the passkey for an assertion, and sends the write once
 * more with `STEP_UP_HEADER`. The server makes every decision. This module holds the calls to the
 * three cp-auth-api step-up routes, the reader of the 403 body, and `runWithStepUp`, the four steps
 * that every write runs.
 *
 * Who calls it: `StepUpDialog.tsx` (the card, the dialog and the provider), `StepUpPage.tsx`, and
 * the WS-9a kernel-rights client (`src/api/kernelRights.api.ts`) through `stepUpHeaders` and
 * `useStepUp()`.
 *
 * Security rules (`design-docs/WS-9.md` section 5):
 *
 * 1. Only an `ApiError` 403 whose body `stepUpRequiredBodySchema` accepts starts a step-up (S-02).
 * 2. A request id must be a lower-case UUID before any use. A malformed id sends no request (S-03).
 * 3. One call gets one step-up and one second send (S-04).
 */
import type { AuthenticationResponseJSON } from '@simplewebauthn/browser';
import {
  STEP_UP_HEADER,
  stepUpRequestViewSchema,
  stepUpRequiredBodySchema,
  type StepUpRequestView,
  type StepUpRequiredBody,
} from '@zion/sdk-contracts';
import { ApiError, apiFetch } from '../http/client';

/**
 * How one step-up ended for the console.
 *
 * - `approved`: the operator approved with a passkey, and one use is open for 600 s;
 * - `denied`: the operator denied it, or the server refused the operator (a 403, or a 422
 *   `operator_inactive` on the assertion);
 * - `cancelled`: the operator closed the dialog;
 * - `expired`: the request ran out (404 or 410 on a read, or the status `expired`);
 * - `consumed`: the approved operation ran once already.
 */
export type StepUpOutcome = 'approved' | 'denied' | 'cancelled' | 'expired' | 'consumed';

/**
 * The step-up of a write ended with an outcome other than `approved`. `runWithStepUp` throws it, and
 * the write does not repeat. A form names the outcome and keeps the operator's input.
 */
export class StepUpNotApprovedError extends Error {
  /**
   * @param outcome the outcome of the step-up, never `approved`.
   * @param requestId the lower-case UUID of the step-up request.
   */
  constructor(
    readonly outcome: Exclude<StepUpOutcome, 'approved'>,
    readonly requestId: string,
  ) {
    super(`The step-up ended with the outcome ${outcome}`);
    this.name = 'StepUpNotApprovedError';
  }
}

/**
 * The second send of a write, with an approved request id, answered a second `step_up_required`.
 * `runWithStepUp` throws it and opens no second step-up (S-04). A form asks for a new send.
 */
export class StepUpRepeatedError extends Error {
  /** @param requestId the lower-case UUID of the first step-up request. */
  constructor(readonly requestId: string) {
    super('The server asked for a second step-up');
    this.name = 'StepUpRepeatedError';
  }
}

/** What a step-up read shows when a 2xx body fails `stepUpRequestViewSchema`. */
export const STEP_UP_RESPONSE_ERROR_MESSAGE = 'The step-up answer did not match the step-up contract';

/** The lower-case RFC 4122 grammar that the CP gives every step-up request id. */
const STEP_UP_REQUEST_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** The cp-auth-api collection of CP step-up requests. */
const STEP_UP_REQUESTS_PATH = '/v1/auth/step-up/requests';

/**
 * True when `value` is a lower-case UUID: 36 characters, the hex digits `0-9a-f` in the groups
 * 8-4-4-4-12. An upper-case digit, a brace, a space or any other text answers `false`. Pure.
 */
export function isStepUpRequestId(value: string): boolean {
  return STEP_UP_REQUEST_ID_PATTERN.test(value);
}

/**
 * The `step_up_required` body of a failed call, or `null`.
 *
 * It answers the body only for an `ApiError` with status 403 whose body `stepUpRequiredBodySchema`
 * accepts. The schema admits a lower-case UUID `stepUpRequestId` alone. Every other value answers
 * `null`: another status, another 403 body (for example `kernel_origin_required`), a body with an
 * extra key, and a value that is not an `ApiError`. It never throws. Pure.
 */
export function stepUpRequiredOf(error: unknown): StepUpRequiredBody | null {
  if (!(error instanceof ApiError) || error.status !== 403) return null;
  const parsed = stepUpRequiredBodySchema.safeParse(error.body);
  return parsed.success ? parsed.data : null;
}

/**
 * The headers that a write sends beside its body. `null` gives `{}`, so the first send carries no
 * step-up. An id gives `{ 'x-zion-step-up': id }` (`STEP_UP_HEADER` of `@zion/sdk-contracts`).
 *
 * @throws Error('invalid step-up request id') for an id that `isStepUpRequestId` refuses, so a
 *   malformed id never reaches the wire.
 */
export function stepUpHeaders(stepUpId: string | null): Record<string, string> {
  if (stepUpId === null) return {};
  if (!isStepUpRequestId(stepUpId)) throw new Error('invalid step-up request id');
  return { [STEP_UP_HEADER]: stepUpId };
}

/**
 * The cp-auth-api path of one step-up request: `/v1/auth/step-up/requests/<id>`.
 *
 * @throws Error('invalid step-up request id') for an id that `isStepUpRequestId` refuses. No
 *   encode is necessary, because the grammar holds no reserved character.
 */
export function stepUpRequestPath(id: string): string {
  if (!isStepUpRequestId(id)) throw new Error('invalid step-up request id');
  return `${STEP_UP_REQUESTS_PATH}/${id}`;
}

/**
 * Parse a step-up view out of a 2xx body.
 *
 * @throws Error(STEP_UP_RESPONSE_ERROR_MESSAGE) with the `ZodError` as its `cause` when the body
 *   fails `stepUpRequestViewSchema`. The schema refuses options whose `userVerification` is not
 *   `required` (S-09), a summary with a control or bidi code point, and options beside a status
 *   other than `pending`.
 */
function parseView(body: unknown): StepUpRequestView {
  const parsed = stepUpRequestViewSchema.safeParse(body);
  if (!parsed.success) throw new Error(STEP_UP_RESPONSE_ERROR_MESSAGE, { cause: parsed.error });
  return parsed.data;
}

/**
 * Read one step-up request: `GET /v1/auth/step-up/requests/:id`.
 *
 * @returns the parsed view: the record, the server summary and, while `pending`, the WebAuthn
 *   options. An expired request answers a view with `status: 'expired'` and no options, because
 *   cp-auth-api derives the status from `expires_at` (`design-docs/WS-6.md`, `CpStepUpService.view`).
 * @throws Error('invalid step-up request id') before any request for a malformed id; `ApiError` on
 *   a non-2xx answer (403 from `NoDelegatedPrincipalGuard` for a delegated token, 404
 *   `step_up_not_found` for an unknown id or a request of another operator); the raw failure on a
 *   network fault; Error(STEP_UP_RESPONSE_ERROR_MESSAGE) on a 2xx body the contract refuses.
 */
export async function getStepUpRequest(id: string): Promise<StepUpRequestView> {
  return parseView(await apiFetch<unknown>(stepUpRequestPath(id)));
}

/**
 * Deny one step-up request: `POST /v1/auth/step-up/requests/:id/deny` with the body `{}`.
 *
 * @returns the parsed view, status `denied`.
 * @throws as `getStepUpRequest`; 409 for a request that is not `pending`, 410 for an expired one.
 */
export async function denyStepUpRequest(id: string): Promise<StepUpRequestView> {
  const body = await apiFetch<unknown>(`${stepUpRequestPath(id)}/deny`, { method: 'POST', body: '{}' });
  return parseView(body);
}

/**
 * Approve one step-up request with a passkey assertion: `POST
 * /v1/auth/step-up/requests/:id/assertion` with the body `{ response }`. The console passes the
 * assertion of `startAuthentication` untouched; the server owns its shape.
 *
 * @returns the parsed view, status `approved`.
 * @throws as `getStepUpRequest`; 400 for an assertion that does not verify, 422
 *   `operator_inactive` for an operator that is absent or not `active` (before the status checks),
 *   409 `step_up_already_answered` for a request that is not `pending`, 410 for an expired one.
 */
export async function submitStepUpAssertion(
  id: string,
  response: AuthenticationResponseJSON,
): Promise<StepUpRequestView> {
  const body = await apiFetch<unknown>(`${stepUpRequestPath(id)}/assertion`, {
    method: 'POST',
    body: JSON.stringify({ response }),
  });
  return parseView(body);
}

/** Show the step-up request `requestId` to the operator, and answer how it ended. */
export type StepUpApprover = (requestId: string) => Promise<StepUpOutcome>;

/**
 * Run one write that may owe a step-up. The four steps:
 *
 * 1. It calls `send(null)` and returns the value on success.
 * 2. It rethrows every error for which `stepUpRequiredOf` returns `null`.
 * 3. It calls `approve(id)`. An outcome other than `approved` throws `StepUpNotApprovedError`.
 * 4. It calls `send(id)` once. A second `step_up_required` throws `StepUpRepeatedError`, and every
 *    other result passes through.
 *
 * It holds no state and caches nothing. It never calls `send` more than twice and `approve` more
 * than once.
 *
 * @param send the write; `null` on the first call, the approved request id on the second.
 * @param approve shows the request to the operator; `useStepUp()` gives the dialog.
 */
export async function runWithStepUp<T>(
  send: (stepUpId: string | null) => Promise<T>,
  approve: StepUpApprover,
): Promise<T> {
  let required: StepUpRequiredBody;
  try {
    return await send(null);
  } catch (error) {
    const body = stepUpRequiredOf(error);
    if (body === null) throw error;
    required = body;
  }
  const requestId = required.stepUpRequestId;
  const outcome = await approve(requestId);
  if (outcome !== 'approved') throw new StepUpNotApprovedError(outcome, requestId);
  try {
    return await send(requestId);
  } catch (error) {
    if (stepUpRequiredOf(error) !== null) throw new StepUpRepeatedError(requestId);
    throw error;
  }
}
